import type {
  Calendar,
  CalendarEvent,
  EventPage,
  EventInput,
  EventOccurrence,
  SessionInfo,
  Subscription,
  SubscriptionCreated,
} from '../shared/contracts';
import type { ImportChange, ImportPreview } from './pages/ImportPage';

export type ClientSession = SessionInfo & {
  capabilities: SessionInfo['capabilities'] & { local?: boolean };
};
export type Preferences = {
  view: 'month' | 'week' | 'day' | 'list';
  timezone: string;
  theme: 'system' | 'light' | 'dark';
  calendarIds: string[];
};
export type CalendarSettings = {
  name: string;
  description?: string;
  color?: string;
  visibility?: 'public' | 'private';
  familyOnly?: boolean;
  staffOnly?: boolean;
  staffAccess?: 'all' | 'selected';
  staffDefaultRole?: 'viewer' | 'editor';
};
export type PageDraft = {
  page: EventPage;
  baseVersion: number;
  updatedAt: string;
  revision: number;
};
export type AuthCapabilities = {
  publicParticipation?: boolean;
  parentAccess: boolean;
  email: boolean;
  magicLink: false;
  passkey: boolean;
  security: boolean;
  providers: { google: boolean; microsoft: boolean; apple: boolean };
};
export type SecurityInfo = {
  user: { id: string; email: string; name: string };
  totpEnabled: boolean;
  mfaRequired: boolean;
  requirePasskey: boolean;
  passkeys: { id: string; name: string; created_at: number | string }[];
};
export type AdminOverview = {
  calendars: {
    id: string;
    name: string;
    familyOnly: boolean;
    staffOnly: boolean;
    staffAccess?: 'all' | 'selected';
    staffDefaultRole?: 'viewer' | 'editor';
  }[];
  memberships: {
    calendar_id: string;
    user_id: string;
    role: 'viewer' | 'editor' | 'manager';
    name: string | null;
    email: string | null;
  }[];
  invitations: {
    id: string;
    email: string;
    calendar_id: string;
    expires_at: number;
    accepted_at: number | null;
    revoked_at: number | null;
    created_by_name: string | null;
  }[];
  parentAccessEnabled: boolean;
  staff?: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    approvedGoogleIdentity: boolean;
    role: 'admin' | 'editor' | 'viewer' | null;
    active: boolean;
    accountActive: boolean;
    isSuperAdmin: boolean;
  }[];
};
export type SourceOverview = {
  available: { id: string; name: string }[];
  sources: {
    id: string;
    calendarId: string;
    enabled: boolean;
    lastAttemptAt: string | null;
    lastSuccessAt: string | null;
    error: string | null;
    summary: string | null;
  }[];
};
export type AdminUsers = {
  users: NonNullable<AdminOverview['staff']>;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};
export type EventHistory = {
  eventId: string;
  history: {
    version: number;
    actorId: string;
    actorName: string;
    createdAt: string;
    changes: string[];
  }[];
};

export type EventPlacement = {
  eventId: string;
  version: number;
  ownerCalendarId: string;
  calendarIds: string[];
};
export type DuplicateCandidate = Pick<
  CalendarEvent,
  'id' | 'title' | 'start' | 'end' | 'allDay' | 'timezone' | 'physicalLocation' | 'version'
> & {
  sourceId: string;
  calendarName: string;
};
export type DuplicateLink = {
  canonicalId: string;
  duplicateId: string;
  reviewedBy: string;
  reviewedAt: string;
};

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit, csrfToken?: string | null): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !(init.body instanceof FormData))
    headers.set('content-type', 'application/json');
  if (csrfToken) headers.set('x-csrf-token', csrfToken);
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok)
    throw new ApiRequestError(
      payload.error || `Request failed (${response.status})`,
      response.status,
    );
  return payload;
}

export const api = {
  locations: () =>
    request<{ locations: import('../shared/contracts').SavedLocation[] }>('/api/locations'),
  createLocation: (
    input: Omit<import('../shared/contracts').SavedLocation, 'id'>,
    csrf: string | null,
  ) =>
    request<{ location: import('../shared/contracts').SavedLocation }>(
      '/api/locations',
      { method: 'POST', body: JSON.stringify(input) },
      csrf,
    ),
  updateLocation: (
    id: string,
    input: Omit<import('../shared/contracts').SavedLocation, 'id'>,
    csrf: string | null,
  ) =>
    request<{ location: import('../shared/contracts').SavedLocation }>(
      `/api/locations/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
      csrf,
    ),
  deleteLocation: (id: string, csrf: string | null) =>
    request<{ removed: true }>(
      `/api/locations/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      csrf,
    ),
  session: () => request<ClientSession>('/api/session'),
  calendars: () => request<{ calendars: Calendar[] }>('/api/calendars'),
  events: (from: Date, to: Date, calendars: string[]) => {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    if (calendars.length) query.set('calendars', calendars.join(','));
    return request<{ events: EventOccurrence[] }>(`/api/events?${query}`);
  },
  event: (id: string, recurrenceId?: string) =>
    request<{ event: CalendarEvent }>(
      `/api/events/${encodeURIComponent(id)}${recurrenceId ? `?occurrence=${encodeURIComponent(recurrenceId)}` : ''}`,
    ),
  eventPage: (path: string, occurrence?: string | null) =>
    request<{ event: CalendarEvent }>(
      `/api/event-page?path=${encodeURIComponent(path)}${occurrence ? `&occurrence=${encodeURIComponent(occurrence)}` : ''}`,
    ),
  createEvent: (input: EventInput, csrfToken?: string | null) =>
    request<{ event: CalendarEvent }>(
      '/api/events',
      { method: 'POST', body: JSON.stringify(input) },
      csrfToken,
    ),
  updateEvent: (
    id: string,
    input: EventInput,
    version: number,
    csrfToken?: string | null,
    recurrence?: { scope: 'occurrence' | 'following' | 'series'; recurrenceId: string },
  ) =>
    request<{ event: CalendarEvent }>(
      `/api/events/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ ...input, version, ...recurrence }) },
      csrfToken,
    ),
  changeEventPath: (id: string, version: number, path: string, csrfToken?: string | null) =>
    request<{ event: CalendarEvent }>(
      `/api/events/${encodeURIComponent(id)}/path`,
      { method: 'PUT', body: JSON.stringify({ version, path }) },
      csrfToken,
    ),
  pageDraft: (id: string) =>
    request<{ draft: PageDraft | null }>(`/api/events/${encodeURIComponent(id)}/page-draft`),
  savePageDraft: (
    id: string,
    page: EventPage,
    version: number,
    draftRevision: number,
    csrfToken?: string | null,
  ) =>
    request<{ draft: PageDraft }>(
      `/api/events/${encodeURIComponent(id)}/page-draft`,
      { method: 'PATCH', body: JSON.stringify({ page, version, draftRevision }) },
      csrfToken,
    ),
  publishPage: (id: string, version: number, draftRevision: number, csrfToken?: string | null) =>
    request<{ event: CalendarEvent }>(
      `/api/events/${encodeURIComponent(id)}/publish-page`,
      { method: 'POST', body: JSON.stringify({ version, draftRevision }) },
      csrfToken,
    ),
  createSubscription: (calendarIds: string[], csrfToken?: string | null) =>
    request<SubscriptionCreated>(
      '/api/subscriptions',
      { method: 'POST', body: JSON.stringify({ calendarIds }) },
      csrfToken,
    ),
  subscription: (id: string) =>
    request<{ subscription: Subscription }>(`/api/subscriptions/${encodeURIComponent(id)}`),
  updateSubscription: (id: string, calendarIds: string[], csrfToken?: string | null) =>
    request<{ subscription: Subscription; feedUrl?: string }>(
      `/api/subscriptions/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ calendarIds }) },
      csrfToken,
    ),
  deleteSubscription: (id: string, csrfToken?: string | null) =>
    request<void>(`/api/subscriptions/${encodeURIComponent(id)}`, { method: 'DELETE' }, csrfToken),
  recoverSubscription: (id: string, recoveryCredential: string, csrfToken?: string | null) =>
    request<{ subscription: Subscription; recoveryCredential: string }>(
      `/api/subscriptions/${encodeURIComponent(id)}/recover`,
      { method: 'POST', body: JSON.stringify({ recoveryCredential }) },
      csrfToken,
    ),
  rotateSubscriptionRecovery: (id: string, csrfToken?: string | null) =>
    request<{ recoveryCredential: string }>(
      `/api/subscriptions/${encodeURIComponent(id)}/recovery`,
      { method: 'POST', body: '{}' },
      csrfToken,
    ),
  upload: async (file: File, calendarId: string, csrfToken?: string | null) => {
    const body = new FormData();
    body.append('file', file);
    body.append('calendarId', calendarId);
    return request<{
      asset: { id: string; url: string; type: 'image' | 'pdf'; name: string; alt: string };
    }>('/api/media', { method: 'POST', body }, csrfToken);
  },
  localLogin: (userId: string) =>
    request<ClientSession>('/__local/login', { method: 'POST', body: JSON.stringify({ userId }) }),
  socialLogin: () =>
    request<{ url: string; redirect: boolean }>('/api/auth/sign-in/social', {
      method: 'POST',
      body: JSON.stringify({ provider: 'google', callbackURL: '/' }),
    }),
  authCapabilities: () => request<AuthCapabilities>('/api/auth/capabilities'),
  security: () => request<SecurityInfo>('/api/auth/security'),
  updateSecurity: (requirePasskey: boolean) =>
    request<{ requirePasskey: boolean }>('/api/auth/security', {
      method: 'POST',
      body: JSON.stringify({ requirePasskey }),
    }),
  acceptInvitation: (token: string) =>
    request<{ calendarId: string }>('/api/auth/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  createInvitation: (email: string, calendarId: string) =>
    request<{ id: string; url: string; expiresAt: string }>('/api/auth/invitations', {
      method: 'POST',
      body: JSON.stringify({ email, calendarId }),
    }),
  revokeInvitation: (id: string) =>
    request<void>(`/api/auth/invitations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  admin: () => request<AdminOverview>('/api/admin'),
  adminUsers: (
    filters: {
      page?: number;
      pageSize?: number;
      query?: string;
      role?: 'admin' | 'editor' | 'viewer' | 'none';
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    if (filters.query) params.set('query', filters.query);
    if (filters.role) params.set('role', filters.role);
    const suffix = params.size ? `?${params}` : '';
    return request<AdminUsers>(`/api/admin/users${suffix}`);
  },
  updateStaffGrant: (
    userId: string,
    role: 'admin' | 'editor' | 'viewer',
    active: boolean,
    csrfToken?: string | null,
  ) =>
    request<{ grant: { userId: string; role: string; active: boolean } }>(
      `/api/admin/staff/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ role, active }) },
      csrfToken,
    ),
  updateSuperAdmin: (
    userId: string,
    enabled: boolean,
    expectedEnabled: boolean,
    confirmationEmail: string,
    csrfToken?: string | null,
  ) =>
    request<{ superAdmin: boolean }>(
      `/api/admin/users/${encodeURIComponent(userId)}/super-admin`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled, expectedEnabled, confirmationEmail }),
      },
      csrfToken,
    ),
  updateAdminUser: (userId: string, active: boolean, csrfToken?: string | null) =>
    request<{ account: { userId: string; active: boolean } }>(
      `/api/admin/users/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ active }) },
      csrfToken,
    ),
  deleteAdminUser: (userId: string, confirmationEmail: string, csrfToken?: string | null) =>
    request<{ deleted: true }>(
      `/api/admin/users/${encodeURIComponent(userId)}`,
      { method: 'DELETE', body: JSON.stringify({ confirmationEmail }) },
      csrfToken,
    ),
  addMembership: (calendarId: string, email: string, csrfToken?: string | null) =>
    request<{ membership: { calendarId: string; userId: string; role: string } }>(
      `/api/admin/memberships/${encodeURIComponent(calendarId)}`,
      { method: 'POST', body: JSON.stringify({ email }) },
      csrfToken,
    ),
  updateMembership: (
    calendarId: string,
    userId: string,
    role: 'viewer' | 'editor' | 'manager',
    csrfToken?: string | null,
  ) =>
    request<{ membership: { calendarId: string; userId: string; role: string } }>(
      `/api/admin/memberships/${encodeURIComponent(calendarId)}/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
      csrfToken,
    ),
  removeMembership: (calendarId: string, userId: string, csrfToken?: string | null) =>
    request<{ removed: boolean }>(
      `/api/admin/memberships/${encodeURIComponent(calendarId)}/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
      csrfToken,
    ),
  adminInvitation: (email: string, calendarId: string, csrfToken?: string | null) =>
    request<{ id: string; url: string; expiresAt: number }>(
      '/api/admin/invitations',
      { method: 'POST', body: JSON.stringify({ email, calendarId }) },
      csrfToken,
    ),
  revokeAdminInvitation: (id: string, csrfToken?: string | null) =>
    request<{ revoked: boolean }>(
      `/api/admin/invitations/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      csrfToken,
    ),
  sources: () => request<SourceOverview>('/api/sources'),
  createSourceConfiguration: (
    source: { id: string; name: string; url: string; historyDays?: number },
    csrfToken?: string | null,
  ) =>
    request<{ source: { id: string; name: string } }>(
      '/api/sources/configurations',
      { method: 'POST', body: JSON.stringify(source) },
      csrfToken,
    ),
  updateSource: (id: string, calendarId: string, enabled: boolean, csrfToken?: string | null) =>
    request<{ id: string; calendarId: string; enabled: boolean }>(
      `/api/sources/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ calendarId, enabled, preserveExisting: true }),
      },
      csrfToken,
    ),
  eventHistory: (id: string) =>
    request<EventHistory>(`/api/events/${encodeURIComponent(id)}/history`),
  eventPlacements: (id: string) =>
    request<EventPlacement>(`/api/events/${encodeURIComponent(id)}/placements`),
  updateEventPlacements: (
    id: string,
    version: number,
    calendarIds: string[],
    csrfToken?: string | null,
  ) =>
    request<{ event: CalendarEvent; placements: EventPlacement }>(
      `/api/events/${encodeURIComponent(id)}/placements`,
      { method: 'PUT', body: JSON.stringify({ version, calendarIds }) },
      csrfToken,
    ),
  duplicateCandidates: (from: string, to: string, query: string) => {
    const params = new URLSearchParams({ from, to, query, limit: '50' });
    return request<{ candidates: DuplicateCandidate[] }>(`/api/event-link-candidates?${params}`);
  },
  eventLinks: (eventId: string) =>
    request<{ links: DuplicateLink[]; records: DuplicateCandidate[] }>(
      `/api/event-links?eventId=${encodeURIComponent(eventId)}`,
    ),
  linkEvents: (
    canonical: DuplicateCandidate,
    duplicate: DuplicateCandidate,
    csrfToken?: string | null,
  ) =>
    request<{
      link: DuplicateLink;
      canonicalEvent: CalendarEvent;
      duplicateEvent: CalendarEvent;
    }>(
      '/api/event-links',
      {
        method: 'POST',
        body: JSON.stringify({
          canonicalId: canonical.id,
          duplicateId: duplicate.id,
          canonicalVersion: canonical.version,
          duplicateVersion: duplicate.version,
          reviewed: true,
        }),
      },
      csrfToken,
    ),
  unlinkEvents: (
    link: DuplicateLink,
    canonicalVersion: number,
    duplicateVersion: number,
    csrfToken?: string | null,
  ) =>
    request<{ link: null; canonicalEvent: CalendarEvent; duplicateEvent: CalendarEvent }>(
      `/api/event-links/${encodeURIComponent(link.duplicateId)}`,
      {
        method: 'DELETE',
        body: JSON.stringify({ canonicalVersion, duplicateVersion }),
      },
      csrfToken,
    ),
  localLogout: () => request<void>('/__local/logout', { method: 'POST' }),
  preferences: async () => {
    const result = await request<{ preferences: Partial<Preferences> }>('/api/preferences');
    return {
      preferences: {
        view: result.preferences.view || 'month',
        timezone: result.preferences.timezone || 'America/Los_Angeles',
        theme: result.preferences.theme || 'system',
        calendarIds: result.preferences.calendarIds || [],
      } satisfies Preferences,
    };
  },
  updatePreferences: (preferences: Preferences, csrfToken?: string | null) =>
    request<{ preferences: Preferences }>(
      '/api/preferences',
      { method: 'PATCH', body: JSON.stringify(preferences) },
      csrfToken,
    ),
  previewImport: (input: { calendarId: string; ics: string }, csrfToken?: string | null) =>
    request<ImportPreview>(
      '/api/import/preview',
      { method: 'POST', body: JSON.stringify(input) },
      csrfToken,
    ),
  commitImport: (
    input: {
      calendarId: string;
      ics: string;
      expectedVersions: Pick<ImportChange, 'uid' | 'id' | 'version'>[];
    },
    csrfToken?: string | null,
  ) =>
    request<{ imported: number; updated: number; warnings: string[] }>(
      '/api/import/commit',
      { method: 'POST', body: JSON.stringify(input) },
      csrfToken,
    ),
  createCalendar: (settings: CalendarSettings, csrfToken?: string | null) =>
    request<{ calendar: Calendar }>(
      '/api/calendars',
      { method: 'POST', body: JSON.stringify(settings) },
      csrfToken,
    ),
  updateCalendar: (
    id: string,
    settings: CalendarSettings & { confirmPublishPrivate?: boolean },
    csrfToken?: string | null,
  ) =>
    request<{ calendar: Calendar }>(
      `/api/calendars/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(settings) },
      csrfToken,
    ),
};
